import React from "react";
import styled from "styled-components";
import Layout from "../components/layout";
import Seo from "../components/seo";
import { SectionWrapper } from "../components/common";
import work from "../content/work.json";

const WorkPageWrapper = styled(SectionWrapper)`
  padding: 1em 3em 5em;
  max-width: 100%;

  @media (min-width: 1200px) {
    padding: 1em 4em 5em;
  }

  @media (min-width: 1500px) {
    padding: 1em 6em 5em;
  }

  @media (max-width: 768px) {
    padding: 1em 1.5em 4em;
  }
`;

const PageTitle = styled.h2`
  background: ${({ theme }) => theme.colors.secondaryGradient};
  font-size: 48px;
  padding: 15px 0;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Intro = styled.p`
  max-width: 820px;
  margin-bottom: 2.5em;
  color: #cfcfcf;
`;

const CardList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5em;

  @media (min-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Card = styled.article`
  background-color: #0a0a0a;
  padding: 1.8em 1.8em 1.6em;
  border-left: 3px solid #f17325;

  @media (max-width: 768px) {
    padding: 1.4em;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.6em;
  margin: 0 0 0.2em;
  font-weight: 700;
`;

const CardSubtitle = styled.p`
  font-size: 0.95em !important;
  color: #aaa;
  margin: 0 0 1em;
  font-weight: 400 !important;
`;

const CardBody = styled.p`
  font-size: 1.05em !important;
  line-height: 1.6em !important;
  margin: 0 0 1em;
`;

const CardStack = styled.p`
  font-size: 0.95em !important;
  color: #cfcfcf;
  font-style: italic;
  margin: 0 0 0.8em;

  strong {
    color: #fff;
    font-style: normal;
  }
`;

const CardLink = styled.a`
  color: #f2994a;
  text-decoration: none;
  font-size: 0.95em;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const SectionHeading = styled.h2`
  margin-top: 3em;
  margin-bottom: 0.3em;
  font-size: 2em;
  background: ${({ theme }) => theme.colors.secondaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SectionLead = styled.p`
  color: #888;
  font-style: italic;
  margin-bottom: 1.5em;
  max-width: 720px;
`;

const WorkPage = () => {
  return (
    <Layout>
      <Seo
        title="Work"
        description="Selected work — payment infrastructure, ledger and reconciliation systems, fraud detection, and enterprise platforms."
      />
      <WorkPageWrapper>
        <PageTitle>Work</PageTitle>
        <Intro>
          A selection of the systems I've built and led — payment
          infrastructure, ledger and reconciliation engines, fraud detection,
          and enterprise platforms across financial services.
        </Intro>
        <CardList>
          {work.cards.map(card => (
            <Card key={card.title}>
              <CardTitle>{card.title}</CardTitle>
              <CardSubtitle>{card.subtitle}</CardSubtitle>
              <CardBody>{card.body}</CardBody>
              {card.stack && (
                <CardStack>
                  <strong>Stack:</strong> {card.stack}
                </CardStack>
              )}
              {card.link && (
                <CardLink
                  href={card.link.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {card.link.label}
                </CardLink>
              )}
            </Card>
          ))}
        </CardList>

        {work.talks && work.talks.length > 0 && (
          <>
            <SectionHeading>Speaking & Community</SectionHeading>
            <SectionLead>
              Section that grows as I give more talks. Most recent first.
            </SectionLead>
            <CardList>
              {work.talks.map(talk => (
                <Card key={talk.title}>
                  <CardTitle>{talk.title}</CardTitle>
                  <CardSubtitle>{talk.subtitle}</CardSubtitle>
                  <CardBody>{talk.body}</CardBody>
                  {talk.link && (
                    <CardLink
                      href={talk.link.href}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {talk.link.label}
                    </CardLink>
                  )}
                </Card>
              ))}
            </CardList>
          </>
        )}

        {work.education && work.education.length > 0 && (
          <>
            <SectionHeading>Education</SectionHeading>
            <SectionLead>Where I trained, formally.</SectionLead>
            <CardList>
              {work.education.map(item => (
                <Card key={item.title}>
                  <CardTitle>{item.title}</CardTitle>
                  <CardSubtitle>{item.subtitle}</CardSubtitle>
                  <CardBody>{item.body}</CardBody>
                </Card>
              ))}
            </CardList>
          </>
        )}
      </WorkPageWrapper>
    </Layout>
  );
};

export default WorkPage;
